import axios from 'axios';
import { track } from './analytics/events';
import {
  notifySessionActivity,
  notifySessionRequestFailed,
  notifySessionRequestStarted,
  notifySessionUnavailable,
} from './sessionState';

/**
 * Standard HTTP Handler
 * @type {Object}
 */
/* withCredentials is per-request below; cross-origin APIs (e.g. DataPower) reject preflight when it's on. */
const XHR = axios.create({
  timeout: 180000,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
});

export const isBffUrl = (url = '') => (
  url === '/api/mgo'
  || url.startsWith('/api/mgo/')
  || url.startsWith('/api/mgo?')
);

const removeAuthorizationHeader = (headers = {}) => {
  Object.keys(headers).forEach((header) => {
    if (header.toLowerCase() === 'authorization') {
      delete headers[header];
    }
  });
};

/*
 * Credentials on for relative URLs only; off for absolute cross-origin APIs.
 * Browser-supplied bearer tokens are never sent to the BFF. The BFF derives
 * its downstream token from the HttpOnly session instead.
 */
export const prepareRequest = (req) => {
  const url = req.url || '';
  req.withCredentials = url.startsWith('/') && !url.startsWith('//');

  if (isBffUrl(url)) {
    removeAuthorizationHeader(req.headers);
    notifySessionRequestStarted();
  }

  return req;
};

XHR.interceptors.request.use(prepareRequest);

/**
 * Session-gate error code returned by the BFF SessionGateErrorHandler.
 * This indicates a real missing/expired BFF session that requires reauthentication.
 */
const SESSION_GATE_CODE = 'BFF_SESSION_REQUIRED';

/**
 * Determines if a 403 response is a session-gate rejection (session expired/invalid)
 * vs an unrelated 403 (CSRF failure, downstream authorization, /metrics route).
 *
 * Per the BFF session-denial contract (MGOD-12149), session-gate 403s include:
 * - Body: { "code": "BFF_SESSION_REQUIRED", "message": "An authenticated session is required" }
 * - Header: X-MGO-Auth-Error: BFF_SESSION_REQUIRED
 *
 * 403s without this code must NOT trigger session recovery.
 *
 * @param {Object} response - Axios response object
 * @returns {boolean}
 */
export const isSessionGate403 = (response) => {
  if (response?.status !== 403) {
    return false;
  }

  // Check header first as fallback for empty/sanitized error bodies
  const headerCode = response?.headers?.['x-mgo-auth-error'];
  const bodyCode = response?.data?.code;
  const code = headerCode || bodyCode || '';

  return code === SESSION_GATE_CODE;
};

export const isSessionUnavailableResponse = (url, response) => {
  if (!isBffUrl(url)) {
    return false;
  }

  if (response?.status === 401) {
    return true;
  }

  return isSessionGate403(response);
};

export const isSessionActivityResponse = (url, response) => (
  isBffUrl(url)
  && Boolean(response)
  && !isSessionUnavailableResponse(url, response)
);

export const updateSessionState = (url, response) => {
  if (!isBffUrl(url)) return;

  if (isSessionUnavailableResponse(url, response)) {
    notifySessionUnavailable();
  } else if (isSessionActivityResponse(url, response)) {
    notifySessionActivity();
  } else {
    // No response means there is no evidence the request reached the BFF.
    notifySessionRequestFailed();
  }
};

// Pair every request-start notification in prepareRequest with a completion,
// including callers that use the exported Axios instance directly.
XHR.interceptors.response.use(
  (response) => {
    updateSessionState(response.config?.url, response);
    return response;
  },
  (error) => {
    updateSessionState(error.config?.url, error.response);
    return Promise.reject(error);
  },
);

const controller = new AbortController();
/**
 * Pass data from response to promise
 * @param  {Object} data [description]
 * @return {Object}      [description]
 */
const responseHandler = ({ data }) => data;

XHR.interceptors.request.use((req) => {
  if (req?.url === '/mgo-api/api/v1/coa') {
    const coa = localStorage.getItem('ajs_user_id');
    if (coa) {
      controller.abort();
      return req;
    }
  }
  return req;
});

/**
 * Pass errors to promise
 * @param  {???} error [description]
 * @return {String}       [description]
 */
const errorHandler = ({ config, response, ...error }) => {
  let { msg, code } = error;

  if (!response) {
    track('mgoApiErrors', {
      page: window.location.pathname,
      message: 'Unknown Error',
      requestType: error.requestType,
      endpoint: error.endpoint,
    });
  }

  if (response?.status === 400 && !response.data) {
    if (config?.url.indexOf('/coa/')) {
      Object.assign(response, {
        data: {
          exception: 'CoaExpired',
        },
      });
    }
  }

  if (!msg) {
    msg = 'There was an issue with your request. Please try again.';
  }

  if (!code) {
    code = '???';
  }

  return { error: msg, xhr: response?.data, raw: response };
};

/**
 * Post to XHR
 * @param  {String} url    [description]
 * @param  {Object} data    [description]
 * @param  {Object} params [description]
 * @param  {Object} headers [description]
 * @return {Promise}       [description]
 */
export const post = (url, data, params, headers) => XHR.post(
  url,
  data,
  { ...params },
  { ...headers },
)
  .then(responseHandler)
  .catch((e) => {
    e.endpoint = url;
    e.requestType = 'POST';
    return errorHandler(e);
  });

/**
 * Put to XHR
 * @param  {String} url    [description]
 * @param  {Object} params [description]
 * @param  {Object} headers [description]
 * @return {Promise}       [description]
 */
export const put = (url, params, headers) => XHR.put(url, { ...params }, { ...headers })
  .then(responseHandler)
  .catch((e) => {
    e.endpoint = url;
    e.requestType = 'PUT';
    return errorHandler(e);
  });

/**
 * Get to XHR
 * @param  {[type]} url    [description]
 * @param  {[type]} params [description]
 * @return {[type]}        [description]
 */
export const get = (url, params, headers) => XHR.get(url, {
  ...params,
  ...headers,
})
  .then(responseHandler)
  .catch((e) => {
    e.endpoint = url;
    e.requestType = 'GET';
    return errorHandler(e);
  });

/**
 * Delete to XHR
 * @param  {[type]} url    [description]
 * @param  {[type]} params [description]
 * @return {[type]}        [description]
 */
export const del = (url, params, headers) => XHR.delete(url, {
  ...params,
  ...headers,
})
  .then(responseHandler)
  .catch((e) => {
    e.endpoint = url;
    e.requestType = 'DELETE';
    return errorHandler(e);
  });

export default XHR;
